import type { DriveAuthSession } from '../types';

export interface CloudBackupSettings {
  enabled: boolean;
  updatedAt?: string;
  deletedCount?: number;
  failedDeleteCount?: number;
}

export interface CloudBackupFile {
  id: string;
  hash: string;
  name: string;
  mimeType: string;
  size: number;
  updatedAt: string;
}

export class CloudBackupApiError extends Error {
  code?: string;
  status?: number;
  retryAfterMs?: number;
  constructor(message: string, options?: { code?: string; status?: number; retryAfterMs?: number }) {
    super(message);
    this.name = 'CloudBackupApiError';
    this.code = options?.code;
    this.status = options?.status;
    this.retryAfterMs = options?.retryAfterMs;
  }
}

const API_BASE = '/api/backup';

const authHeaders = (session: DriveAuthSession) => ({
  Authorization: `Bearer ${session.idToken || session.accessToken}`,
  'x-access-token': session.accessToken,
  'Content-Type': 'application/json',
});

const fileToBase64 = async (file: File): Promise<string> => {
  const bytes = await file.arrayBuffer();
  let binary = '';
  const chunkSize = 0x8000;
  const u8 = new Uint8Array(bytes);
  for (let i = 0; i < u8.length; i += chunkSize) {
    const chunk = u8.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const base64ToFile = (dataBase64: string, name: string, mimeType: string): File => {
  const binary = atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], name, { type: mimeType });
};

export const cloudBackupService = {
  async getSettings(session: DriveAuthSession): Promise<CloudBackupSettings> {
    const res = await fetch(`${API_BASE}/settings`, {
      method: 'GET',
      headers: authHeaders(session),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      if (res.status === 429 || res.status === 503) {
        throw new CloudBackupApiError(
          body?.message || body?.error || 'Cloud backup is temporarily unavailable.',
          { code: body?.error, status: res.status, retryAfterMs: body?.retryAfterMs },
        );
      }
      return { enabled: false };
    }
    return res.json();
  },

  async setEnabled(session: DriveAuthSession, enabled: boolean): Promise<CloudBackupSettings> {
    const res = await fetch(`${API_BASE}/settings`, {
      method: 'POST',
      headers: authHeaders(session),
      body: JSON.stringify({ enabled }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new CloudBackupApiError(
        body?.message || body?.error || 'Failed to update cloud backup settings.',
        { code: body?.error, status: res.status, retryAfterMs: body?.retryAfterMs },
      );
    }
    return res.json();
  },

  async listFiles(session: DriveAuthSession): Promise<CloudBackupFile[]> {
    const res = await fetch(`${API_BASE}/local`, {
      method: 'GET',
      headers: authHeaders(session),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      if (res.status === 429 || res.status === 503) {
        throw new CloudBackupApiError(
          body?.message || body?.error || 'Cloud backup is temporarily unavailable.',
          { code: body?.error, status: res.status, retryAfterMs: body?.retryAfterMs },
        );
      }
      return [];
    }
    const payload = await res.json();
    return Array.isArray(payload?.files) ? payload.files : [];
  },

  async uploadLocalFile(
    session: DriveAuthSession,
    payload: { id: string; hash: string; file: File }
  ): Promise<void> {
    const dataBase64 = await fileToBase64(payload.file);
    const res = await fetch(`${API_BASE}/local`, {
      method: 'POST',
      headers: authHeaders(session),
      body: JSON.stringify({
        id: payload.id,
        hash: payload.hash,
        name: payload.file.name,
        mimeType: payload.file.type || 'application/octet-stream',
        size: payload.file.size,
        dataBase64,
      }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new CloudBackupApiError(
        body?.message || body?.error || 'Failed to upload local backup file.',
        { code: body?.error, status: res.status, retryAfterMs: body?.retryAfterMs },
      );
    }
  },

  async downloadLocalFile(session: DriveAuthSession, file: CloudBackupFile): Promise<File> {
    const res = await fetch(`${API_BASE}/local?hash=${encodeURIComponent(file.hash)}`, {
      method: 'GET',
      headers: authHeaders(session),
    });
    if (!res.ok) {
      throw new Error(`Failed to restore backup file: ${file.name}`);
    }
    const payload = await res.json();
    return base64ToFile(String(payload?.dataBase64 || ''), file.name, payload?.mimeType || file.mimeType);
  },
};
